import produce from 'immer';
import { Action } from 'redux-actions';
import storage from 'redux-persist/lib/storage';
import { ReducerFactory } from 'redux-actions-ts-reducer';
import { persistReducer, PersistConfig } from 'redux-persist';
import { LOCATION_CHANGE, RouterState } from 'connected-react-router';

import { GlobalState } from '../../../../rootReducer';
import { LoadableData } from '../../../../common/utils/LoadableData';
import { addNewRow, getInvoiceRowsLoadable, removeRow, updateRow } from './InvoiceRowsActions';
import { BaseSearch, InvoiceRowDTO, PagedListContainer } from '../../../../services/types/ApiTypes';

export const createRequest = (page = 1, count = 15): InvoiceRowsSearchParams => ({
    PagingOptions: {
        Count: count,
        Page: page,
    },
    Restrictions: [],
    SortItems: [],
});

export const createNewRow = (invoiceId: number, totalCount: number, vatRate?: number): InvoiceRowDTO => ({
    Id: 0,
    InvoiceId: invoiceId,
    Description: '-',
    SumWithoutVat: '0.00',
    VAT: '0.00',
    VatRate: vatRate || 0,
    Total: '0.00',
    ItemDetailInfo: [
        {
            Id: null,
            ItemUnit: null,
            ItemAmount: '0',
            ItemPrice: '0.00',
            InvoiceRowId: 0,
            IsNew: true,
        },
    ],
    InvoiceCountInUse: null,
    IsNew: false,
    SellerProductId: null,
    SerialNumber: null,
    BuyerProductId: null,
    CustomerRef: null,
    TaricCode: null,
    OrderNo: totalCount,
    InvoiceRowDiscounts: [],
});

export type InvoiceRowsSearchParams = BaseSearch;

class State {
    invoiceRowsLoadable = new LoadableData<PagedListContainer<InvoiceRowDTO>, BaseSearch>();
    searchParams: InvoiceRowsSearchParams = {
        ...createRequest(),
    };
}

const reducer = new ReducerFactory(new State())
    .addReducer(
        getInvoiceRowsLoadable.request,
        (state, action): State => {
            const invoiceRowsLoadable = state.invoiceRowsLoadable.withLoading(action.payload);
            return {
                ...state,
                searchParams: action.payload,
                invoiceRowsLoadable,
            };
        },
    )
    .addReducer(
        getInvoiceRowsLoadable.success,
        (state, action): State => {
            const invoiceRowsLoadable = state.invoiceRowsLoadable.withPayloadIfRequestEquals(action.payload);
            return {
                ...state,
                searchParams: action.payload.request,
                invoiceRowsLoadable,
            };
        },
    )
    .addReducer(
        getInvoiceRowsLoadable.error,
        (state, action): State => {
            return {
                ...state,
                searchParams: action.payload.request,
                invoiceRowsLoadable: state.invoiceRowsLoadable.withErrorIfRequestEquals(action.payload),
            };
        },
    )
    .addReducer(
        updateRow,
        produce((draft, action: Action<{ result: InvoiceRowDTO; itemToUpdate: number }>) => {
            const index = draft.invoiceRowsLoadable.payload.Items.findIndex((i: InvoiceRowDTO) => {
                if (i.Id > 0) {
                    return i.Id === action.payload.itemToUpdate;
                }
                return i.Id === 0;
            });
            draft.invoiceRowsLoadable.payload.Items[index] = {
                ...action.payload.result,
            };
        }),
    )
    .addReducer(
        addNewRow,
        produce((draft, action) => {
            draft.invoiceRowsLoadable.payload.Items.push(action.payload);
            draft.invoiceRowsLoadable.payload.TotalCount++;
        }),
    )
    .addReducer(
        removeRow,
        produce((draft, action) => {
            draft.invoiceRowsLoadable.payload.Items = draft.invoiceRowsLoadable.payload.Items.filter((i: InvoiceRowDTO) => i.Id !== action.payload);
        }),
    )
    .addReducer(
        LOCATION_CHANGE,
        (state, action: Action<RouterState>): State => {
            if (/\/invoiceconfirmation/.test(action.payload.location.pathname)) {
                return {
                    ...state,
                };
            }
            return {
                ...new State(),
            };
        },
    )
    .toReducer();

const persistConfig: PersistConfig<State> = {
    storage,
    key: 'settings/invoice-rows',
    whitelist: ['searchParams'],
};

export default persistReducer(persistConfig, reducer);

export { State as InvoiceRowsViewState };

export const selectInvoiceRowsListLoadable = (state: GlobalState) => state.invoiceRows.invoiceRowsLoadable;
export const selectInvoiceRowsListSearchParams = (state: GlobalState) => state.invoiceRows.searchParams;
